import React from 'react';
import { DateUtils, EnumHelper, ITaskRunner, Misc, ModalUtils } from '@singularsystems/neo-core';
import { Neo, NeoGrid, Views } from '@singularsystems/neo-react';
import InvestmentComponentVM from './InvestmentComponentVM';
import { observer } from 'mobx-react';
import { DateCycleEditComponent } from '../../InvestmentSetup/Components/DateCycleEdit';
import DateCycle from '../../../Models/Dividends/DateCycle';
import ShareTransactionCommand, { TransactionType } from '../../../Models/Transactions/Commands/ShareTransactionCommand';
import { IInvestmentHost } from '../../../Models/Investment/IInvestmentHost';
import { IInvestmentHostApiClient } from '../../../ApiClients/IInvestmentHostApiClient';
import ShareTransactionLookup from '../../../Models/Transactions/Lookups/ShareTransactionLookup';
import DateCycleDropDown from './DateCycleDropDown';
import Investment from '../../../Models/Investment/Investment';
import { ValidationDisplay } from '../../../Components/ValidationDisplay';
import { CompoundingType } from '../../../Models/Dividends/CompoundingType.enum';
import { DayOfMonthType } from '../../../Models/Dividends/DayOfMonthType.enum';
import IVisibilityOptions from '../../../Models/Config/FieldConfig/IInvestmentFields';
import { isHidden } from '../../../Models/Config/FieldConfig/FieldSetting.enum';
import { DateCycleType } from '../../../Models/Dividends/DateCycleType';
import { BusinessDayConvention } from '../../../Models/Dividends/BusinessDayConvention.enum';
import TextValues from '../../../Services/TextValues';

interface IInvestmentComponentProps {
    taskRunner?: ITaskRunner;
    investmentHost: IInvestmentHost;
    apiClient: IInvestmentHostApiClient;
    onTransactionChange: () => void;
    additionalCalcOptions?: React.ReactNode;
    fieldSettings: IVisibilityOptions;
    isLink?: boolean;
    /** Causes the "Place notice" button to appear on the transactions grid. Making the transaction respect the notice period. */
    noticePeriodId?: number | null;
}

@observer
export default class InvestmentComponent extends Views.ViewComponentBase<IInvestmentComponentProps, InvestmentComponentVM> {

    constructor(props: IInvestmentComponentProps) {
        super(props, () => new InvestmentComponentVM(props.investmentHost, props.apiClient, props.taskRunner, props.onTransactionChange));

    }

    public render() {
        const viewModel = this.viewModel,
            investment = viewModel.investment,
            investmentLookups = investment.lookup,
            calculationOptions = investment.calculationOptions,
            lastAllocationDate = viewModel.investmentHost.commonFields.lastAllocationDate;
        const { additionalCalcOptions, fieldSettings, isLink, investmentHost, noticePeriodId } = this.props;
        const balance = investmentLookups.shareTransactions.sum(c => c.amount);
        const preventAddTransaction = viewModel.parentIsRestricted || (viewModel.config?.authorisationRequiredForTransactions && !viewModel.parentIsAuthorised);

        return (
            <div>
                <Neo.Card title="Calculation Parameters" className="mt-0">
                    <Neo.GridLayout>
                        <div>
                            <Neo.FormGroupInline bind={investment.meta.initialEndDate}
                                dateProps={{ onDateSelected: () => investment.onInitialEndDateChanged() }}
                                hidden={isHidden(fieldSettings.initialEndDate)}
                                disabled={investmentLookups.shareTransactions.length > 0} />
                            <Neo.FormGroupInline bind={investment.meta.endDate} disabled={investment.isNew} />
                            <Neo.FormGroupInline bind={investment.meta.valuePerShare} disabled={investmentLookups.shareTransactions.length > 0} />
                            <Neo.FormGroupInline bind={investment.meta.parValuePerShare} numProps={{ formatString: "##0.00" }} hidden={isHidden(fieldSettings.parValuePerShare) || viewModel.isInvestor} />
                            <Neo.FormGroupInline bind={investment.meta.premiumPerShare} numProps={{ formatString: "##0.00" }} hidden={isHidden(fieldSettings.premiumPerShare) || viewModel.isInvestor} />
                            <DateCycleDropDown viewModel={viewModel} id={investment.meta.dividendCycleId} display={investment.lookup.meta.dividendCycleName}
                                navigation={investment.meta.dividendCycle} filterType={DateCycleType.Dividends} disabled={viewModel.investment.lookup.hasDividends} label={TextValues.distribution + " Cycle"}/>

                            <Neo.FormGroupInline label="Payment Date Convention" hidden={isHidden(fieldSettings.paymentDateConvention)}
                               footerText={`Determines how to adjust ${TextValues.distributionLowerCase} payment and cycle dates that fall on non-working days.`}>
                                <Neo.AutoCompleteDropDown bind={calculationOptions.meta.paymentDateConvention} items={EnumHelper.asList(BusinessDayConvention)}
                                    isDisabled={investment.lookup.hasDividends} />
                            </Neo.FormGroupInline>
                            <Neo.FormGroupInline
                                bind={investment.calculationOptions.meta.paymentDateOffset}
                                footerText={investment.calculationOptions.paymentDateConvention === BusinessDayConvention.Actual ? "Number of days to delay the payment date." : "Number of business days to delay the payment date"}
                                isDisabled={investment.lookup.hasDividends} />

                            <Neo.FormGroup bind={investment.meta.completeDividendCycle} label={`Complete ${TextValues.distribution} Cycle?`}
                                footerText={`Indicates that the final ${TextValues.distributionLowerCase} date should be according to the ${TextValues.distributionLowerCase} cycle, rather than the investment end date.`}
                                hidden={isHidden(fieldSettings.completeDividendCycle)} />

                            {additionalCalcOptions}
                        </div>
                        <div>
                            <Neo.FormContext allDisabled={investment.lookup.hasDividends}>

                                <DateCycleDropDown viewModel={viewModel} id={investment.meta.compoundingCycleId} disabled={viewModel.investment.lookup.hasDividends} display={investment.lookup.meta.compoundingCycleName}
                                    navigation={investment.meta.compoundingCycle} filterType={DateCycleType.Compounding} onCycleSelected={cycle => investment.endOfMonthReporting = cycle?.dayOfMonthType === DayOfMonthType.LastDay}
                                    hidden={isHidden(fieldSettings.compoundingCycleId)} label="Compounding Cycle"/>

                                <Neo.FormGroupInline label={`Compounding on ${TextValues.distribution}`} hidden={isHidden(fieldSettings.compoundOnDividend)}>
                                    <Neo.AutoCompleteDropDown bind={calculationOptions.meta.compoundOnDividend}
                                        items={EnumHelper.asList(CompoundingType)} />
                                </Neo.FormGroupInline>

                                <Neo.FormGroup bind={calculationOptions.meta.compoundOnRateChange} label="Compound on Rate Change?"
                                    footerText="Treat a rate change date (own or linked rate) as a compounding date."
                                    hidden={isHidden(fieldSettings.compoundOnRateChange)} />

                                <Neo.FormGroup bind={calculationOptions.meta.rateGrossedUp} label="Rate Grossed Up?"
                                    footerText="Indicates if the Investment will apply Gross Up calculations or not."
                                    hidden={isHidden(fieldSettings.rateGrossedUp)} />
                            </Neo.FormContext>

                            <Neo.FormGroup
                                bind={investment.meta.endOfMonthReporting} label="End of Month Reporting?"
                                hidden={isHidden(fieldSettings.endOfMonthReporting)}
                                footerText="Indicates that monthly reports for this investment will be from month end to month end instead of month start to month start." />

                        </div>
                    </Neo.GridLayout>
                </Neo.Card>

                <Neo.GridLayout lg={2}>
                    <Neo.Card title={investment.initialTransaction === null ? "Transactions" : "Initial transaction"}>
                        {investmentLookups.shareTransactions.length > 0 &&
                            <NeoGrid.Grid items={investmentLookups.shareTransactions} className="mb-2">
                                {(transaction, transactionMeta) => (
                                    <NeoGrid.Row>
                                        <NeoGrid.Column display={transactionMeta.transactionDate} />
                                        {viewModel.isLiquidityProvider && <NeoGrid.Column display={transactionMeta.description} />}
                                        <NeoGrid.Column display={transactionMeta.noOfShares} />
                                        <NeoGrid.Column display={transactionMeta.amount} numProps={{ currencySymbol: "R" }} sum />
                                        <NeoGrid.ButtonColumn deleteButton={{
                                            onClick: () => this.deleteTransaction(transaction),
                                            disabled: viewModel.parentIsRestricted || (lastAllocationDate ? transaction.transactionDate <= lastAllocationDate : false),
                                            tooltip: (lastAllocationDate && transaction.transactionDate <= lastAllocationDate) ?
                                                `Cannot delete before ${DateUtils.format(lastAllocationDate)} because share allocations have been created up to this date.` : ""
                                        }} />
                                    </NeoGrid.Row>
                                )}
                            </NeoGrid.Grid>}

                        {investment.initialTransaction === null && !isLink &&
                            <div>
                                <Neo.Button
                                    icon="plus"
                                    disabled={preventAddTransaction}
                                    tooltip={preventAddTransaction ? viewModel.createTransactionDisabledTooltipText : ""}
                                    onClick={() => viewModel.addTransaction()}>New Transaction
                                </Neo.Button>
                                {noticePeriodId && !investment.isNew && balance > 0 && !viewModel.investmentHost.commonFields.isCeded &&
                                    <Neo.Button
                                        icon="plus"
                                        className="ms-2"
                                        disabled={preventAddTransaction}
                                        tooltip={preventAddTransaction ? viewModel.createTransactionDisabledTooltipText : ""}
                                        onClick={() => viewModel.placeNotice(noticePeriodId)}>Place notice
                                    </Neo.Button>}
                            </div>}

                        {investment.initialTransaction &&
                            <div>
                                {this.renderNewTransaction(investment.initialTransaction)}

                                <Neo.Button icon="trash" size="sm" variant="danger" onClick={() => investment.initialTransaction = null}>Cancel</Neo.Button>
                            </div>}
                    </Neo.Card>
                    <Neo.Card title="Rates">

                        <Neo.FormGroupInline label="Rate Type">
                            <Neo.AutoCompleteDropDown
                                bind={calculationOptions.meta.interestRateTypeId}
                                disabled={investment.investmentRates.activeLength > 0 || investment.lookup.hasDividends}
                                items={viewModel.dataCache.interestRateTypes}
                                onItemSelected={rateType => calculationOptions.interestRateType = rateType ?? null}
                                descriptionMember="description"
                                filterPredicate={item => item.interestRateTypeId === calculationOptions.interestRateTypeId || (item.appliesToCounterparty(viewModel.isInvestor) && item.appliesToExemptType(viewModel.isExempt))}
                                placeholder="Own rates" />
                        </Neo.FormGroupInline>

                        {calculationOptions.spreadRequired &&
                            <Neo.FormGroupInline bind={calculationOptions.meta.spread} label="Credit Adjustment Spread" appendText="%" disabled={viewModel.investment.lookup.hasDividends} />
                        }

                        <Neo.FormGroupInline label="Custom Calculation">
                            <Neo.AutoCompleteDropDown
                                bind={calculationOptions.meta.calculationTypeId}
                                disabled={investment.investmentRates.activeLength > 0 || investment.lookup.hasDividends}
                                items={viewModel.dataCache.interestRateCalculationTypes}
                                filterPredicate={item => calculationOptions.interestRateType?.interestCalculationType == null || item.interestCalculationType === calculationOptions.interestRateType.interestCalculationType}
                                descriptionMember="description"
                                placeholder="Normal (Based on rate type)" />
                        </Neo.FormGroupInline>

                        <ValidationDisplay rule={Investment.ratesRequiredRule} model={investment} />

                        {investment.ratesRequired &&
                            <NeoGrid.Grid items={investment.investmentRates} addButton={{ onClick: () => viewModel.addRate() }}>
                                {(rate, rateMeta) => (
                                    <NeoGrid.Row>
                                        <NeoGrid.Column bind={rateMeta.effectiveDate} width={200} />
                                        <NeoGrid.Column bind={rateMeta.rate} appendText="%" />
                                        <NeoGrid.Column bind={rateMeta.rateCalculationTypeId}
                                            label="Custom Calc."
                                            width={200}
                                            select={{
                                                itemSource: viewModel.dataCache.interestRateCalculationTypes,
                                                filterPredicate(item) { return calculationOptions.interestRateType?.interestCalculationType == null || item.interestCalculationType === calculationOptions.interestRateType.interestCalculationType },
                                                nullText: "Normal" }} />
                                        <NeoGrid.ButtonColumn showDelete />
                                    </NeoGrid.Row>
                                )}
                            </NeoGrid.Grid>}

                    </Neo.Card>

                    {investmentLookups.pendingTransactionRequests.length > 0 &&
                        <Neo.Card title="Pending Transaction Requests">
                            <NeoGrid.Grid items={investmentLookups.pendingTransactionRequests} className="mb-2">
                                {(request, requestMeta) => (
                                    <NeoGrid.Row>
                                        <NeoGrid.Column display={requestMeta.requestedDate} />
                                        <NeoGrid.Column display={requestMeta.requestedAmount} numProps={{ currencySymbol: "R" }} sum />
                                        <NeoGrid.Column display={requestMeta.allocatedAmount} numProps={{ currencySymbol: "R" }} sum />
                                    </NeoGrid.Row>
                                )}
                            </NeoGrid.Grid>
                        </Neo.Card>}
                </Neo.GridLayout>

                <Neo.Modal
                    title="Edit date cycle"
                    bindModel={viewModel.dateCycleVM.meta.editingDateCycle}
                    size="lg"
                    acceptButton={viewModel.dateCycleVM.isReadOnly ? undefined : { onClick: () => viewModel.saveDateCycle() }}>
                    {(dateCycle: DateCycle) =>
                        <Neo.FormContext allReadOnly={viewModel.dateCycleVM.isReadOnly}>
                            <DateCycleEditComponent viewModel={viewModel.dateCycleVM} dateCycle={dateCycle} inModal />
                        </Neo.FormContext>
                    }
                </Neo.Modal>

                <Neo.Modal
                    title="Add transaction"
                    bindModel={viewModel.meta.newTransaction}
                    size="lg"
                    acceptButton={{ onClick: () => viewModel.saveTransaction(), disabled: viewModel.newTransactionTask.isBusy }}>
                    {(transaction: ShareTransactionCommand) =>
                        this.renderNewTransaction(transaction)}
                </Neo.Modal>

            </div>
        );
    }

    private renderNewTransaction(transaction: ShareTransactionCommand) {
        const autoConvertNoOfShares = transaction.investmentValues.valuePerShare <= 1;

        return (
            <Neo.Loader task={this.viewModel.newTransactionTask}>
                {transaction.noticePeriodDays !== null &&
                    <Neo.FormGroupInline bind={transaction.meta.noticeDate}
                        dateProps={{ onDateSelected: () => this.viewModel.onNoticeDateChanged(transaction), formatString: "dd MMM yyyy (ddd)" }} />}
                <Neo.FormGroupInline bind={transaction.meta.transactionDate}
                    dateProps={{ minDate: transaction.minDate, onDateSelected: () => this.viewModel.onTransactionDateChanged(transaction), formatString: "dd MMM yyyy (ddd)" }} />
                <Neo.FormGroupInline bind={transaction.meta.transactionType}
                    select={{ items: EnumHelper.asList(TransactionType), onItemSelected: () => transaction.onNoOfSharesChanged() }}
                    isReadOnly={transaction.noticePeriodDays !== null} />
                <Neo.FormGroupInline
                    bind={transaction.meta.amountPositive}
                    onBlur={() => {
                        if (autoConvertNoOfShares) {
                            transaction.calculateNoOfShares();
                        }
                    }}
                    append={<Neo.Button size="sm" hidden={autoConvertNoOfShares || transaction.noOfShares === 0} icon="calculator" tooltip="Calculate Amount" onClick={() => transaction.calculateAmount()} />}
                    numProps={{ format: this.viewModel.investment.valuePerShare < 1 ? Misc.NumberFormat.Decimals : Misc.NumberFormat.NoDecimals }}
                    prependText="R" />

                <Neo.FormGroupInline
                    bind={transaction.meta.noOfSharesPositive}
                    onBlur={() => {
                        transaction.onNoOfSharesChanged();
                        if (autoConvertNoOfShares) {
                            transaction.calculateAmount();
                        }
                    }}
                    append={<Neo.Button size="sm" hidden={autoConvertNoOfShares || transaction.amountPositive.equals(0)} icon="calculator" tooltip="Calculate No of shares" onClick={() => transaction.calculateNoOfShares()} />}
                    numProps={{ format: Misc.NumberFormat.NoDecimals }} />
            </Neo.Loader>
        )
    }

    private deleteTransaction(transaction: ShareTransactionLookup) {
        ModalUtils.showYesNoDismissible("Delete transaction", "Are you sure you want to delete this transaction?",
            () => this.viewModel.deleteTransaction(transaction));
    }
}